import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { ContentApi } from "../../../../shared/api/contentApi";
import {
    UiDialogRoot,
    UiDialogContent,
    UiDialogTitle,
    UiDialogDescription,
    UiDialogClose,
    UiDialogHeader,
    UiDialogBody,
    UiDialogFooter,
} from "../../../../components/ui/ant/Dialog";
import {
  Form,
  Input,
  Select,
  Switch,
  App
} from 'antd';
import "antd/dist/reset.css";
import ValueInput from "../../../../shared/paramEditor/ValueInput";
import { TypeSettings, inputToDefaultValue, withoutEmptyValues } from "./SystemNameEditParameter";


const VALUE_TYPES = [
    "NUMERIC",
    "STRING",
    "BOOLEAN",
    "DATE",
    "JSON",
    "ARRAY",
    "ENUM",
    "ENTITY_REF",
    "URL",
    "COLOR",
    "DURATION",
    "ASSET_REF",
];

export default function ParameterCreateDialog({ open, onClose, onCreated, projectId, versionId }) {
    const [systemName, setSystemName] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [valueTypeCode, setValueTypeCode] = useState("STRING");
    const [defaultValueInput, setDefaultValueInput] = useState("");
    const [options, setOptions] = useState({});
    const [isRequired, setIsRequired] = useState(false); 

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setSystemName("");
        setName("");
        setDescription("");
        setValueTypeCode("STRING");
        setDefaultValueInput("");
        setOptions({});
        setError("");
        setSaving(false);
    }, [open]);

    const canSubmit = useMemo(
        () => !!systemName.trim() && !!name.trim() && !!valueTypeCode && !saving,
        [systemName, name, valueTypeCode, saving],
    );

    const onSubmit = async (e) => {
        e?.preventDefault?.();
        if (!canSubmit) return;

        setSaving(true);
        setError("");

        try {
            const cleanedOptions = withoutEmptyValues({
                ...options,
                display: withoutEmptyValues(options.display),
            });
            if (!Object.keys(cleanedOptions.display || {}).length) delete cleanedOptions.display;
            const defaultValue = inputToDefaultValue(valueTypeCode, defaultValueInput, cleanedOptions);
            
            const payload = {
                systemName: systemName.trim(),
                name: name.trim(),
                description: description.trim() || null,
                valueTypeCode,
                optionsJson: Object.keys(cleanedOptions).length ? JSON.stringify(cleanedOptions) : null,
                defaultValueJson: defaultValue === null ? null : JSON.stringify(defaultValue),
                isRequired
            };
            const created = await ContentApi.createParameter(payload);
            onCreated?.(created);
            onClose?.();
        } catch (e) {
            setError(e?.message || "Не удалось создать параметр");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <App>
        <UiDialogRoot open={open} onOpenChange={(next) => !next && onClose?.()}>
            <UiDialogContent size="lg">
                <UiDialogHeader>
                    <div>
                        <UiDialogTitle>Создать параметр</UiDialogTitle>
                        <UiDialogDescription>Справочник параметров текущей версии</UiDialogDescription>
                    </div>
                    <UiDialogClose showIcon />
                </UiDialogHeader>

                <UiDialogBody>
                    {error && (
                        <div className="mb-4 rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                            {error}
                        </div>
                    )}
                    <Form layout="vertical" className="space-y-4" onSubmit={onSubmit}>
                        <Form.Item label="systemName" className="flex flex-col">
                            <Input
                                className="input"
                                value={systemName}
                                onChange={(e) => setSystemName(e.target.value)}
                                placeholder="parameter_damage"
                                required
                            />
                        </Form.Item>

                        <Form.Item label="Название" className="flex flex-col">
                            <Input
                                className="input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Название параметра, например Урон"
                                required
                            />
                        </Form.Item>

                        <Form.Item label="Описание" className="flex flex-col">
                            <Input.TextArea
                                className="input"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Описание параметра, например урон наносимый пользователю"
                            />
                        </Form.Item>

                        <Form.Item label="Обязательное" className="">
                            <Switch 
                                size="3" 
                                checked={isRequired} 
                                onChange={(checked) => setIsRequired(checked)}>
                            </Switch>
                        </Form.Item>

                        <Form.Item label="Тип значения">
                            <Select
                                value={valueTypeCode}
                                onChange={(value) => {
                                    setValueTypeCode(value);
                                    setDefaultValueInput("");
                                    setOptions({});
                                }}
                                options={VALUE_TYPES.map((value) => ({ value, label: value }))}
                            />
                        </Form.Item>

                        <Form.Item label="Значение по умолчанию" className="flex flex-col">
                            <ValueInput
                                valueType={valueTypeCode}
                                options={options}
                                value={defaultValueInput}
                                onChange={setDefaultValueInput}
                            />
                        </Form.Item>

                        <TypeSettings valueType={valueTypeCode} options={options} onChange={setOptions} />
                    </Form>
                </UiDialogBody>

                <UiDialogFooter>
                    <button type="button" className="btn btn-soft-accent" onClick={onSubmit} disabled={!canSubmit}>
                        {saving ? "Создаем..." : "Создать"}
                    </button>
                    <button type="button" className="btn btn-outline" onClick={onClose}>
                        Отмена
                    </button>
                </UiDialogFooter>
            </UiDialogContent>
        </UiDialogRoot>
        </App>
    );
}

ParameterCreateDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onCreated: PropTypes.func,
    projectId: PropTypes.string,
    versionId: PropTypes.string,
};
